// frontend/src/modules/Properties/components/VRPanoramaUploader.tsx
import { useState, useEffect, useRef, useCallback } from 'react';
import {
  Card,
  Stack,
  Group,
  Button,
  Text,
  Select,
  Image,
  Badge,
  Paper,
  ActionIcon,
  ThemeIcon,
  Tooltip,
  Center,
  Box,
  Loader,
  Alert,
  Divider,
  AspectRatio
} from '@mantine/core';
import {
  IconUpload,
  IconTrash,
  IconCheck,
  IconX,
  IconInfoCircle,
  Icon360View,
  IconPhoto
} from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { notifications } from '@mantine/notifications';
import { modals } from '@mantine/modals';
import { useMediaQuery } from '@mantine/hooks';
import { propertiesApi } from '@/api/properties.api';

interface VRPanorama {
  id: number;
  location_type: string;
  location_number: number | null;
  panorama_image: string | null;
  sort_order: number;
}

interface TempVRPanorama {
  location_type: string;
  location_number: number;
  file: File;
  preview: string;
}

interface VRPanoramaUploaderProps {
  propertyId: number;
  onUpdate: () => void;
  viewMode?: boolean;
  onChange?: (panoramas: TempVRPanorama[]) => void;
}

const VRPanoramaUploader = ({
  propertyId,
  onUpdate,
  viewMode = false,
  onChange
}: VRPanoramaUploaderProps) => {
  const { t } = useTranslation();
  const isMobile = useMediaQuery('(max-width: 768px)');

  const fileInputRef = useRef<HTMLInputElement>(null);

  const [panoramas, setPanoramas] = useState<VRPanorama[]>([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);

  const [tempPanoramas, setTempPanoramas] = useState<TempVRPanorama[]>([]);
  const isCreatingMode = propertyId === 0;

  const [locationType, setLocationType] = useState('bedroom');
  const [locationNumber, setLocationNumber] = useState<number>(1);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string>('');

  const locationTypes = [
    'bedroom', 'bathroom', 'living_room', 'kitchen', 'pool',
    'terrace', 'wardrobe', 'gym', 'sauna', 'balcony',
    'dining_room', 'office', 'laundry', 'garage', 'entrance'
  ];

  const numberedLocations = ['bedroom', 'bathroom', 'wardrobe', 'balcony', 'terrace'];

  useEffect(() => {
    if (isCreatingMode && onChange) {
      onChange(tempPanoramas);
    }
  }, [tempPanoramas, isCreatingMode, onChange]);

  useEffect(() => {
    return () => {
      tempPanoramas.forEach(panorama => {
        URL.revokeObjectURL(panorama.preview);
      });
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
    };
  }, []);

  useEffect(() => {
    if (propertyId && !isCreatingMode) {
      loadPanoramas();
    }
  }, [propertyId]);

  const loadPanoramas = async () => {
    setLoading(true);
    try {
      const { data } = await propertiesApi.getVRPanoramas(propertyId);
      setPanoramas(data.data || []);
    } catch (error: any) {
      notifications.show({
        title: t('errors.generic'),
        message: error.response?.data?.message || t('vr.errors.loadFailed'),
        color: 'red',
        icon: <IconX size={18} />
      });
    } finally {
      setLoading(false);
    }
  };

  const handleFileChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];

    if (!file) {
      return;
    }

    if (file.size > 50 * 1024 * 1024) {
      notifications.show({
        title: t('errors.generic'),
        message: t('vr.errors.fileTooLarge'),
        color: 'red',
        icon: <IconX size={18} />
      });
      return;
    }

    if (!file.type.startsWith('image/')) {
      notifications.show({
        title: t('errors.generic'),
        message: t('vr.errors.invalidFileType'),
        color: 'red',
        icon: <IconX size={18} />
      });
      return;
    }

    // Clear old preview
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
    }

    const newPreviewUrl = URL.createObjectURL(file);
    setSelectedFile(file);
    setPreviewUrl(newPreviewUrl);

    // Reset input
    e.target.value = '';
  }, [previewUrl, t]);

  const handleClearPreview = useCallback(() => {
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
    }
    setSelectedFile(null);
    setPreviewUrl('');
  }, [previewUrl]);

  const handleUploadClick = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const handleUpload = async () => {
    if (!selectedFile) {
      notifications.show({
        title: t('vr.errors.fillAllFields'),
        message: t('vr.editor.uploadHintSingle'),
        color: 'orange',
        icon: <IconInfoCircle size={18} />
      });
      return;
    }

    if (isCreatingMode) {
      const tempPanorama: TempVRPanorama = {
        location_type: locationType,
        location_number: locationNumber,
        file: selectedFile,
        preview: previewUrl
      };

      setTempPanoramas([...tempPanoramas, tempPanorama]);

      notifications.show({
        title: t('common.success'),
        message: t('vr.success.addedToList'),
        color: 'green',
        icon: <IconCheck size={18} />
      });

      setSelectedFile(null);
      setPreviewUrl('');
      setLocationNumber(1);
      return;
    }

    try {
      setUploading(true);

      const formData = new FormData();
      formData.append('panorama', selectedFile);
      formData.append('location_type', locationType);
      formData.append('location_number', String(locationNumber));

      await propertiesApi.createVRPanorama(propertyId, formData);

      notifications.show({
        title: t('common.success'),
        message: t('vr.success.created'),
        color: 'green',
        icon: <IconCheck size={18} />
      });

      setSelectedFile(null);
      setPreviewUrl('');
      setLocationNumber(1);

      loadPanoramas();
      onUpdate();
    } catch (error: any) {
      notifications.show({
        title: t('errors.generic'),
        message: error.response?.data?.message || t('vr.errors.createFailed'),
        color: 'red',
        icon: <IconX size={18} />
      });
    } finally {
      setUploading(false);
    }
  };

  const handleRemoveTempPanorama = (index: number) => {
    const panoramaToDelete = tempPanoramas[index];
    URL.revokeObjectURL(panoramaToDelete.preview);
    setTempPanoramas(tempPanoramas.filter((_, i) => i !== index));

    notifications.show({
      title: t('common.success'),
      message: t('vr.success.removedFromList'),
      color: 'green',
      icon: <IconCheck size={18} />
    });
  };

  const handleDelete = async (panoramaId: number) => {
    try {
      await propertiesApi.deleteVRPanorama(propertyId, panoramaId);
      notifications.show({
        title: t('common.success'),
        message: t('vr.success.deleted'),
        color: 'green',
        icon: <IconCheck size={18} />
      });
      loadPanoramas();
      onUpdate();
    } catch (error: any) {
      notifications.show({
        title: t('errors.generic'),
        message: error.response?.data?.message || t('vr.errors.deleteFailed'),
        color: 'red',
        icon: <IconX size={18} />
      });
    }
  };

  const getLocationLabel = (type: string, number: number | null) => {
    const label = t(`vr.locations.${type}`);
    return number ? `${label} ${number}` : label;
  };

  return (
    <Stack gap="lg">
      <Card shadow="sm" padding="lg" radius="md" withBorder>
        <Stack gap="lg">
          {/* Header */}
          <Group justify="space-between">
            <Group gap="sm">
              <ThemeIcon size="lg" radius="md" variant="light" color="indigo">
                <Icon360View size={20} />
              </ThemeIcon>
              <div>
                <Text fw={600} size="lg">
                  {t('vr.editor.title360')}
                </Text>
                {(panoramas.length > 0 || tempPanoramas.length > 0) && (
                  <Text size="sm" c="dimmed">
                    {t('vr.editor.panoramasCount', { count: panoramas.length + tempPanoramas.length })}
                  </Text>
                )}
              </div>
            </Group>

            {(panoramas.length > 0 || tempPanoramas.length > 0) && (
              <Badge size="lg" variant="filled" color="indigo">
                {panoramas.length + tempPanoramas.length}
              </Badge>
            )}
          </Group>

          {loading && (
            <Center p="xl">
              <Loader size="xl" variant="dots" />
            </Center>
          )}

          {isCreatingMode && tempPanoramas.length > 0 && (
            <Alert icon={<IconInfoCircle size={18} />} color="orange" variant="light">
              <Text size="sm">
                {t('vr.editor.tempStorageInfo', { count: tempPanoramas.length })}
              </Text>
            </Alert>
          )}

          {/* Upload Form */}
          {!viewMode && !loading && (
            <>
              <Divider />

              <Paper p="md" radius="md" withBorder>
                <Stack gap="md">
                  <Group gap="sm">
                    <ThemeIcon size="md" radius="md" variant="light" color="indigo">
                      <IconUpload size={16} />
                    </ThemeIcon>
                    <Text fw={600}>{t('vr.editor.newPanorama360')}</Text>
                  </Group>

                  {/* Location Type */}
                  <Select
                    label={t('vr.editor.locationType')}
                    placeholder={t('vr.editor.selectLocationType')}
                    value={locationType}
                    onChange={(value) => setLocationType(value || 'bedroom')}
                    data={locationTypes.map(type => ({
                      value: type,
                      label: t(`vr.locations.${type}`)
                    }))}
                    styles={{
                      input: { fontSize: '16px' }
                    }}
                  />

                  {/* Location Number */}
                  {numberedLocations.includes(locationType) && (
                    <Select
                      label={t('vr.editor.locationNumber')}
                      placeholder={t('vr.editor.selectLocationNumber')}
                      value={String(locationNumber)}
                      onChange={(value) => setLocationNumber(Number(value))}
                      data={[1, 2, 3, 4, 5].map(num => ({
                        value: String(num),
                        label: String(num)
                      }))}
                      styles={{
                        input: { fontSize: '16px' }
                      }}
                    />
                  )}

                  {/* Upload Panorama */}
                  <Stack gap="xs">
                    <Text fw={500} size="sm">
                      {t('vr.editor.uploadPanorama360')}
                    </Text>

                    {/* Hidden file input */}
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      style={{ display: 'none' }}
                      onChange={handleFileChange}
                    />

                    {/* Preview or Upload Area */}
                    {previewUrl ? (
                      <Box pos="relative">
                        <AspectRatio ratio={2}>
                          <Image
                            src={previewUrl}
                            alt="360 Panorama Preview"
                            radius="md"
                            style={{
                              border: '2px solid var(--mantine-color-indigo-6)',
                              objectFit: 'cover'
                            }}
                          />
                        </AspectRatio>
                        <ActionIcon
                          color="red"
                          variant="filled"
                          size="lg"
                          onClick={handleClearPreview}
                          style={{
                            position: 'absolute',
                            top: 8,
                            right: 8
                          }}
                        >
                          <IconTrash size={18} />
                        </ActionIcon>
                        <Badge
                          variant="filled"
                          color="indigo"
                          size="lg"
                          style={{
                            position: 'absolute',
                            bottom: 8,
                            left: 8
                          }}
                        >
                          <Group gap={4}>
                            <Icon360View size={14} />
                            {t('vr.editor.panoramaReady')}
                          </Group>
                        </Badge>
                      </Box>
                    ) : (
                      <Paper
                        p="xl"
                        radius="md"
                        withBorder
                        style={{
                          cursor: 'pointer',
                          borderStyle: 'dashed',
                          borderWidth: 2
                        }}
                        onClick={handleUploadClick}
                      >
                        <Center>
                          <Stack align="center" gap="md">
                            <ThemeIcon size={60} radius="xl" variant="light" color="indigo">
                              <IconPhoto size={30} />
                            </ThemeIcon>
                            <div>
                              <Text size="lg" fw={500} ta="center">
                                {t('vr.editor.dropPanorama')}
                              </Text>
                              <Text size="sm" c="dimmed" ta="center">
                                {t('vr.editor.panoramaFormat')}
                              </Text>
                            </div>
                          </Stack>
                        </Center>
                      </Paper>
                    )}

                    <Alert icon={<IconInfoCircle size={18} />} color="blue" variant="light">
                      {t('vr.editor.panoramaHint')}
                    </Alert>
                  </Stack>

                  {/* Upload Button */}
                  <Button
                    size="lg"
                    variant="gradient"
                    gradient={{ from: 'indigo', to: 'cyan', deg: 90 }}
                    leftSection={<IconUpload size={20} />}
                    onClick={handleUpload}
                    disabled={!selectedFile}
                    loading={uploading}
                    fullWidth
                  >
                    {uploading
                      ? t('vr.editor.uploading')
                      : isCreatingMode
                      ? t('vr.editor.addToList')
                      : t('vr.editor.create')
                    }
                  </Button>
                </Stack>
              </Paper>
            </>
          )}

          {/* Temporary Panoramas List */}
          {isCreatingMode && tempPanoramas.length > 0 && (
            <>
              <Divider />

              <Stack gap="md">
                <Text fw={600}>
                  {t('vr.editor.tempPanoramas')} ({tempPanoramas.length})
                </Text>

                {tempPanoramas.map((panorama, index) => (
                  <Paper key={index} p="md" radius="md" withBorder style={{ borderColor: 'var(--mantine-color-orange-6)' }}>
                    <Stack gap="md">
                      <Group justify="space-between" wrap="nowrap">
                        <Group gap="sm">
                          <ThemeIcon size="lg" radius="md" variant="light" color="orange">
                            <Icon360View size={20} />
                          </ThemeIcon>
                          <div>
                            <Group gap="xs">
                              <Text fw={600} size={isMobile ? 'sm' : 'md'}>
                                {getLocationLabel(panorama.location_type, panorama.location_number)}
                              </Text>
                              <Badge variant="filled" color="orange" size="sm">
                                {t('vr.editor.tempStorage')}
                              </Badge>
                            </Group>
                          </div>
                        </Group>

                        {!viewMode && (
                          <Tooltip label={t('common.delete')}>
                            <ActionIcon
                              color="red"
                              variant="light"
                              size="lg"
                              onClick={() => handleRemoveTempPanorama(index)}
                            >
                              <IconTrash size={18} />
                            </ActionIcon>
                          </Tooltip>
                        )}
                      </Group>

                      <AspectRatio ratio={3}>
                        <Image
                          src={panorama.preview}
                          alt={getLocationLabel(panorama.location_type, panorama.location_number)}
                          radius="md"
                          style={{
                            border: '2px dashed var(--mantine-color-orange-6)',
                            objectFit: 'cover'
                          }}
                        />
                      </AspectRatio>
                    </Stack>
                  </Paper>
                ))}
              </Stack>
            </>
          )}

          {/* Existing Panoramas List */}
          {!loading && panoramas.length > 0 && (
            <>
              <Divider />

              <Stack gap="md">
                <Text fw={600}>
                  {isCreatingMode
                    ? t('vr.editor.uploadedPanoramas')
                    : t('vr.editor.existingPanoramas')
                  } ({panoramas.length})
                </Text>

                {panoramas.map(panorama => (
                  <Paper key={panorama.id} p="md" radius="md" withBorder>
                    <Stack gap="md">
                      <Group justify="space-between" wrap="nowrap">
                        <Group gap="sm">
                          <ThemeIcon size="lg" radius="md" variant="light" color="indigo">
                            <Icon360View size={20} />
                          </ThemeIcon>
                          <div>
                            <Text fw={600} size={isMobile ? 'sm' : 'md'}>
                              {getLocationLabel(panorama.location_type, panorama.location_number)}
                            </Text>
                            <Badge variant="light" color="indigo" size="sm">
                              {t('vr.editor.panorama360')}
                            </Badge>
                          </div>
                        </Group>

                        {!viewMode && (
                          <Tooltip label={t('common.delete')}>
                            <ActionIcon
                              color="red"
                              variant="light"
                              size="lg"
                              onClick={() => {
                                modals.openConfirmModal({
                                  title: t('vr.confirm.delete'),
                                  children: <Text>{t('common.confirmDelete')}</Text>,
                                  labels: { confirm: t('common.yes'), cancel: t('common.no') },
                                  confirmProps: { color: 'red' },
                                  onConfirm: () => handleDelete(panorama.id)
                                });
                              }}
                            >
                              <IconTrash size={18} />
                            </ActionIcon>
                          </Tooltip>
                        )}
                      </Group>

                      {panorama.panorama_image && (
                        <AspectRatio ratio={3}>
                          <Image
                            src={panorama.panorama_image}
                            alt={getLocationLabel(panorama.location_type, panorama.location_number)}
                            radius="md"
                            style={{ objectFit: 'cover' }}
                          />
                        </AspectRatio>
                      )}
                    </Stack>
                  </Paper>
                ))}
              </Stack>
            </>
          )}

          {/* Empty State */}
          {!loading && panoramas.length === 0 && tempPanoramas.length === 0 && viewMode && (
            <Paper p="xl" radius="md" withBorder>
              <Center>
                <Stack align="center" gap="md">
                  <ThemeIcon size={80} radius="xl" variant="light" color="gray">
                    <Icon360View size={40} />
                  </ThemeIcon>
                  <Text size="lg" c="dimmed" ta="center">
                    {t('vr.editor.noPanoramas')}
                  </Text>
                </Stack>
              </Center>
            </Paper>
          )}
        </Stack>
      </Card>
    </Stack>
  );
};

export default VRPanoramaUploader;
